import { AlertTriangle, CalendarClock, CalendarX2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { daysUntil } from "./opportunitiesService";
import { URGENT_DAYS } from "./opportunityConstants";

interface DeadlineBadgeProps {
  deadline: string | null;
  /** Terminal rows keep the date visible but drop the alarm styling. */
  muted?: boolean;
  className?: string;
}

/**
 * The single most important control in this module. FutuMed closed eight weeks
 * before anyone noticed; a deadline that does not shout is the failure mode.
 */
export function DeadlineBadge({ deadline, muted = false, className }: DeadlineBadgeProps) {
  const days = daysUntil(deadline);

  if (days === null) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 text-xs text-muted-foreground",
          className,
        )}
      >
        <CalendarClock className="h-3 w-3" />
        No deadline
      </span>
    );
  }

  const overdue = days < 0;
  const urgent = !overdue && days <= URGENT_DAYS;

  const label = overdue
    ? `${Math.abs(days)}d overdue`
    : days === 0
      ? "Due today"
      : `${days}d left`;

  const Icon = overdue ? CalendarX2 : urgent ? AlertTriangle : CalendarClock;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums",
        muted && "bg-muted text-muted-foreground",
        !muted && overdue && "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
        !muted && urgent && "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
        !muted && !overdue && !urgent && "bg-muted text-muted-foreground",
        className,
      )}
      title={deadline ?? undefined}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}
